import { useState, type FormEvent } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";

export default function AdminLogin() {
  const { user, loading, signIn, resetPassword } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<"login" | "reset">("login");
  const [resetSent, setResetSent] = useState(false);

  if (!loading && user) {
    return <Navigate to="/admin" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (mode === "reset") {
      setSubmitting(true);
      const { error: resetError } = await resetPassword(email);
      setSubmitting(false);
      if (resetError) {
        setError(resetError);
      } else {
        setResetSent(true);
      }
      return;
    }

    if (!email || !password) {
      setError("Introduce tu email y contraseña.");
      return;
    }

    setSubmitting(true);
    const { error: signInError } = await signIn(email, password);
    setSubmitting(false);
    if (signInError) {
      setError("Credenciales incorrectas. Verifica tu email y contraseña.");
    }
  }

  return (
    <div className="min-h-screen bg-background-50 digital-grid flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white border border-background-200 rounded-lg p-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-code-s-slash-line text-white text-xl" />
            </div>
          </div>

          <h1 className="font-heading text-xl font-semibold text-center text-foreground-950">
            MARK DIRECT COMPANY
          </h1>
          <p className="text-sm text-center text-foreground-500 mb-8">
            Panel de administración
          </p>

          {resetSent ? (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-accent-50 text-accent-600 flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-send-line text-xl" />
              </div>
              <p className="text-sm text-foreground-700 mb-2">
                Revisa tu correo electrónico.
              </p>
              <p className="text-xs text-foreground-500 mb-6">
                Te enviamos un enlace para restablecer tu contraseña.
              </p>
              <button
                onClick={() => {
                  setMode("login");
                  setResetSent(false);
                }}
                className="text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Volver a iniciar sesión
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="admin-login">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  Email o usuario
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  className="w-full px-3.5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                  placeholder="tu@correo.com"
                />
              </div>

              {mode === "login" && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Contraseña
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="w-full px-3.5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition"
                    placeholder="••••••••"
                  />
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                  <i className="ri-error-warning-line mt-0.5" />
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {submitting ? (
                  <>
                    <span className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                    Procesando…
                  </>
                ) : mode === "login" ? (
                  "Iniciar sesión"
                ) : (
                  "Enviar enlace de recuperación"
                )}
              </button>
            </form>
          )}

          {!resetSent && (
            <div className="mt-6 text-center">
              {mode === "login" ? (
                <button
                  onClick={() => {
                    setMode("reset");
                    setError(null);
                  }}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              ) : (
                <button
                  onClick={() => {
                    setMode("login");
                    setError(null);
                  }}
                  className="text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Volver a iniciar sesión
                </button>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-foreground-500 mt-6">
          <Link to="/" className="hover:text-primary-600 transition-colors">
            ← Volver al sitio público
          </Link>
        </p>
      </div>
    </div>
  );
}